import { memo } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { CreatePost } from "@/components/common/CreatePost";
import { StreamActionToggle } from "@/features/study-group-stream/components/StreamActionToggle";
import { StreamSearch } from "@/features/study-group-stream/components/StreamSearch";
import type { StreamSearchCriteriaState } from "@/lib/utils/streamSearchUtils";
import type { StreamMode } from "@/features/study-group-stream/types/streamMode";
import type { SearchAuthorOption } from "@/features/study-group-stream/utils/streamSearchFilterOptions";

export type StreamModePanelHandlers = {
  onSearchMode: () => void;
  onCreateMode: () => void;
  onSearch: (searchCriteriaState: StreamSearchCriteriaState) => void;
  onExitMode: () => void;
  onCreatePost: (message: string, files?: File[]) => Promise<void>;
  onCreateError?: (error: unknown) => void;
  onAuthorFilterOpen?: () => void | Promise<void>;
};

export type StreamModePanelState = {
  mode: StreamMode;
  studyGroupId: number | null;
  searchHits: number;
  authorOptions: SearchAuthorOption[];
  authorLabelById: Map<string, string>;
  isAuthorOptionsLoading?: boolean;
  canCreate?: boolean;
  createPostClassName?: string;
};

export type StreamModePanelProps = StreamModePanelState &
  StreamModePanelHandlers;

const DEFAULT_CREATE_POST_CLASS =
  "p-2 animate-in fade-in slide-in-from-top-2 duration-200";
const SEARCH_PANEL_CLASS = DEFAULT_CREATE_POST_CLASS;

const StreamModePanelRaw: React.FC<StreamModePanelProps> = ({
  mode,
  studyGroupId,
  searchHits,
  authorOptions,
  authorLabelById,
  isAuthorOptionsLoading = false,
  canCreate = true,
  createPostClassName = DEFAULT_CREATE_POST_CLASS,
  onSearchMode,
  onCreateMode,
  onSearch,
  onExitMode,
  onCreatePost,
  onCreateError,
  onAuthorFilterOpen,
}) => {
  const { t } = useTranslation();
  const isSearchMode = mode === "search";
  const isCreateMode = mode === "create";

  if (mode === "view") {
    return canCreate ? (
      <StreamActionToggle
        onSearchMode={onSearchMode}
        onCreateMode={onCreateMode}
      />
    ) : null;
  }

  if (isSearchMode) {
    return (
      <div className={SEARCH_PANEL_CLASS}>
        <StreamSearch
          onSearch={onSearch}
          onExitSearch={onExitMode}
          totalHits={searchHits}
          className="mb-0"
          authorOptions={authorOptions}
          authorLabelById={authorLabelById}
          onAuthorFilterOpen={onAuthorFilterOpen}
          isAuthorOptionsLoading={isAuthorOptionsLoading}
        />
      </div>
    );
  }

  if (isCreateMode) {
    return canCreate ? (
      <div className={createPostClassName}>
        <CreatePost
          studyGroupId={studyGroupId}
          onCreatePost={onCreatePost}
          onCreated={onExitMode}
          onError={onCreateError}
          autoFocus
        />
        <div className="mt-1 flex justify-end">
          <button
            type="button"
            onMouseDown={(e) => {
              // Prevent editor blur/layout shift before click is committed.
              e.preventDefault();
            }}
            onClick={onExitMode}
            className="rounded-md px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    ) : null;
  }

  return null;
};

export const StreamModePanel = memo(StreamModePanelRaw);
StreamModePanel.displayName = "StreamModePanel";
